import React from 'react';
import { Bell, ChevronDown, Grid3X3, Search } from 'lucide-react';
import LogoSvg from "../../assets/images/brand/milletLogo.svg";
import classNames from "classnames";
import AllSetSvg from "../../assets/images/brand/darkAllSet.svg";
import { useNavbar } from "../navbar/useNavbar";
import { useBreakpoint } from "../../utils/useBreakpoint";
import { LocalStorageKeys } from "../../dictionaries/localstorage";
import { AsBadge, AsButton, MobileNavBar } from "../../subframe";
import SchedulesSvg from "../../assets/icons/navbar/schedules.svg";
import { Path } from "../../path";
import SalesSvg from "../../assets/icons/navbar/sales.svg";
import ClientsSvg from "../../assets/icons/navbar/clients.svg";
import BusinessSettingsSvg from "../../assets/icons/navbar/businessSettings.svg";
import ResellerHomeSvg from "../../assets/icons/navbar/resellerHome.svg";
import { UserRoles } from "../../redux/userSlice";
import { Divider, Dropdown, DropdownItem, DropdownMenu, DropdownTrigger } from "@nextui-org/react";
import { Avatar } from "../avatar/avatar";
import { truncate } from "lodash";
import { titleize } from "../../utils/string";
import { Navbar } from "../navbar/navbar";
import ServicesSvg from "app/assets/icons/navbar/services.svg";
import { LogoWithName } from "../logoWithName";
import { UserAuth } from 'app/context/AuthContext';
import { ImpersonationBanner } from 'app/components/ImpersonationBanner/ImpersonationBanner';
import { AsIconButton } from 'app/subframe';


interface TopNavigationProps {
    activeView: string;
    onViewChange: (view: string) => void;
}


export interface NavbarProps {
    reseller?: boolean;
}

const regularNavItemsGroup1: any[] = [{
    img: SchedulesSvg, title: "Calendar", to: Path.HOME, iconHoverTitle: "Schedule",
},

{
    img: ClientsSvg, title: "Clients", to: Path.CLIENTS, iconHoverTitle: "Clients",
},
// {img: SalesSvg, title: "Memberships", to: Path.MEMBERSHIPS, iconHoverTitle: "Memberships"},
{
    img: SalesSvg,
    title: "Catalog",
    to: Path.CATALOG,
    iconHoverTitle: "Catalog"
}, { img: SalesSvg, title: "Transactions", to: Path.SALES, iconHoverTitle: "Transactions" },];




const regularNavItemsGroup2: any[] = [

    {
        img: BusinessSettingsSvg, title: "Reports", to: Path.REPORTS, iconHoverTitle: "Settings",
    },
    {
        img: BusinessSettingsSvg, title: "Settings", to: Path.SETTINGS, iconHoverTitle: "Settings",
    },
    // { img: StaffSvg, title: "Staff", to: Path.STAFF, iconHoverTitle: "Staff" },
    // {
    //   img: ServicesSvg,
    //   title: "Services",
    //   to: Path.BUSINESS_SERVICES,
    //   iconHoverTitle: "Services",
    // },
];

const resellerNavItemsGroup1: any[] = [{
    img: ResellerHomeSvg, title: "Merchant Management", to: Path.RESELLER_HOME, iconHoverTitle: "Merchant Management",
},];

const resellerNavItemsGroup2: any[] = [];


const TopNavigation: React.FC<NavbarProps> = ({ reseller = false }) => {

    const {
        navigate, user, logout,
    } = useNavbar();
    const { revert } = UserAuth();

    const { isBelowSm, isAboveSm } = useBreakpoint("sm");
    const impersonationId = localStorage.getItem(LocalStorageKeys.IMPERSONATION_ID);


    const isImpersonating = React.useMemo(() => {
        return user?.reseller && impersonationId;
    }, [user?.reseller, impersonationId]);

    const navbarItemsGroup1 = regularNavItemsGroup1;
    const navbarItemsGroup2 = user?.role === 'admin' ? regularNavItemsGroup2 : [];

    if (isBelowSm) {
        return (<MobileNavBar className="mobile:h-16 mobile:w-full fixed bottom-0 z-50" />);
    }


    const navigationItems = [{ id: 'calendar', label: 'Calendar' }, { id: 'crm', label: 'Clients' }, {
        id: 'memberships',
        label: 'Memberships'
    }, { id: 'catalog', label: 'Catalog' }, { id: 'transactions', label: 'Transactions' }, {
        id: 'reports',
        label: 'Reports'
    }, { id: 'settings', label: 'Settings' },];


    // Create a function to generate dropdown menu items
    const getDropdownMenuItems = () => {
        const menuItems: any = [];

        // Info item (always shown)
        menuItems.push(
            <DropdownItem
                key="info"
                className="!opacity-100 data-[hover=true]:!bg-millet-500"
            >
                <div className="flex flex-col">
                    <div className="flex items-center gap-2 !mb-2">
                        <Avatar
                            src={user.avatar}
                            name={user.name}
                            size={40}
                            className="rounded-sm w-10 h-10"
                        />
                        <div className="flex flex-col">
                            <p className="text-center font-semibold">
                                {truncate(user.name, {
                                    length: 20,
                                })}
                            </p>
                            <AsBadge
                                variant="success"
                                icon={null}
                                iconRight={null}
                                className="w-fit"
                            >
                                {titleize(user.role)}
                            </AsBadge>
                        </div>
                    </div>
                    <Divider className="!bg-neutral-50 mt-1" />
                </div>
            </DropdownItem>
        );


        // Settings item (only shown when not impersonating)
        // if (!isImpersonating) {
        //     menuItems.push(
        //         <DropdownItem
        //             key="settings"
        //             onClick={() => navigate(`${Path.USER}?path=details`)}
        //             className="!p-0 !bg-transparent data-[hover=true]:!bg-millet-500"
        //         >
        //             <AsButton
        //                 leadingIcon={"FeatherCog"}
        //                 variant="ghost"
        //                 className="w-full justify-start items-center"
        //                 text="User setting"
        //                 size="md"
        //             />
        //         </DropdownItem>
        //     );
        // }

        // if (isImpersonating || !user.reseller) {
        //     menuItems.push(
        //         <DropdownItem
        //             key="billing"
        //             className="!p-0 !bg-transparent data-[hover=true]:!bg-millet-500"
        //             onClick={() => navigate(Path.BILLING)}
        //         >
        //             <AsButton
        //                 leadingIcon={"FeatherCreditCard"}
        //                 variant="ghost"
        //                 className="w-full justify-start items-center"
        //                 text="Billing"
        //             />
        //         </DropdownItem>
        //     );
        // }

        // Logout item (always shown)
        menuItems.push(
            <DropdownItem
                key="logout"
                onClick={() => logout()}
                className="!p-0 !bg-transparent data-[hover=true]:!bg-millet-500"
            >
                <AsButton
                    trailingIcon={"FeatherLogOut"}
                    variant="ghost"
                    className="w-full justify-between items-center"
                    text="Log out"
                />
            </DropdownItem>
        );

        return menuItems;
    };


    return (
        <div className="flex flex-col fixed z-[10] w-full">
            <ImpersonationBanner />
            <div className="bg-white border-b border-gray-200 w-full">
                <div className="px-6 py-4">
                    <div className="flex items-center justify-between">
                        {/* Left side - Logo and Navigation */}
                        <div className="flex items-center space-x-8">
                            <div className="flex items-center space-x-2">
                                {/*<img src={LogoSvg} alt="logo" className="w-6 h-7"/>*/}
                                {/*<img*/}
                                {/*    className={classNames("font-semibold duration-300 left-9 w-14",)}*/}
                                {/*    src={AllSetSvg}*/}
                                {/*    alt={"logo"}/>*/}

                                <LogoWithName />
                            </div>

                            <nav className="flex items-center space-x-8 relative">
                                {/*{navigationItems.map((item) => (*/}
                                {/*  <button*/}
                                {/*    key={item.id}*/}
                                {/*    onClick={() => onViewChange(item.id)}*/}
                                {/*    className={`text-sm font-medium transition-colors relative ${*/}
                                {/*      activeView === item.id*/}
                                {/*        ? 'text-blue-600'*/}
                                {/*        : 'text-gray-600 hover:text-gray-900'*/}
                                {/*    }`}*/}
                                {/*  >*/}
                                {/*    {item.label}*/}
                                {/*    {activeView === item.id && (*/}
                                {/*      <div className="absolute -bottom-4 left-0 right-0 h-0.5 bg-blue-600"></div>*/}
                                {/*    )}*/}
                                {/*  </button>*/}
                                {/*))}*/}


                                {navbarItemsGroup1.map((item, index) => {
                                    const isSelected = window.location.pathname === item.to;
                                    // if (user.role !== UserRoles.ADMIN && item.title !== "Schedules") {
                                    //     return null;
                                    // }

                                    return (<div className="relative" key={index}>
                                        <div className="overflow-hidden w-full">
                                            <button
                                                key={index}
                                                // onMouseOver={() => setHoveredNavIcon(item.title)}
                                                onClick={() => navigate(item.to)}
                                                className={classNames("text-sm font-medium transition-colors  ", {
                                                    'text-blue-600': isSelected,
                                                    'text-gray-600 hover:text-gray-900': !isSelected

                                                })}
                                            >
                                                {item.title}
                                                {isSelected && (<div
                                                    className="absolute -bottom-4 left-0 right-0 h-0.5 bg-blue-600"></div>)}
                                            </button>
                                        </div>
                                    </div>);
                                })}

                                {navbarItemsGroup2.map((item, index) => {
                                    const isSelected = window.location.pathname === item.to;
                                    // if (user.role !== UserRoles.ADMIN && item.title !== "Schedules") {
                                    //     return null;
                                    // }
                                    return (<div className="relative" key={index}>
                                        <div className="overflow-auto w-full">
                                            <div className="overflow-hidden w-full">
                                                <button
                                                    key={index}
                                                    onClick={() => navigate(item.to)}
                                                    className={classNames("text-sm font-medium transition-colors", {
                                                        'text-blue-600': isSelected,
                                                        'text-gray-600 hover:text-gray-900': !isSelected
                                                    })}
                                                >
                                                    {item.title}
                                                    {isSelected && (<div
                                                        className="absolute -bottom-4 left-0 right-0 h-0.5 bg-blue-600"></div>)}
                                                </button>
                                            </div>
                                        </div>
                                    </div>);
                                })}


                            </nav>
                        </div>

                        {/* Right side - Search and User */}
                        <div className="flex items-center space-x-4">
                            {/*<div className="relative">*/}
                            {/*    <Search*/}
                            {/*        className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"/>*/}
                            {/*    <input*/}
                            {/*        type="text"*/}
                            {/*        placeholder="Search..."*/}
                            {/*        className="pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent w-64"*/}
                            {/*    />*/}
                            {/*</div>*/}

                            {/*<button className="relative p-2 text-gray-400 hover:text-gray-600">*/}
                            {/*    <Bell className="w-5 h-5"/>*/}
                            {/*    <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>*/}
                            {/*</button>*/}

                            {user?.reseller && <button onClick={() => {
                                navigate(Path.RESELLER_HOME)
                            }} className="p-2 text-gray-400 hover:text-gray-600">
                                <img src={ResellerHomeSvg} width={30} alt={'reseller'} />
                            </button>}

                            {/*<div className="flex items-center space-x-2 pl-4 border-l border-gray-200">*/}
                            {/*    <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center">*/}
                            {/*        <User className="w-4 h-4 text-gray-600"/>*/}
                            {/*    </div>*/}
                            {/*    <span className="text-sm font-medium text-gray-700">Amelia</span>*/}
                            {/*    <ChevronDown className="w-4 h-4 text-gray-400"/>*/}
                            {/*</div>*/}


                            {/* {user?.isImpersonating && (
                                <AsIconButton
                                  leadingIcon="FeatherArrowLeft"
                                  variant="ghost"
                                  onClick={() => revert()}
                                  className="text-amber-600 hover:bg-amber-50"
                                />
                            )} */}

                            <Dropdown
                                classNames={{
                                    base: "w-[234px]",
                                    content: "shadow-none border rounded-md",
                                }}
                            >
                                <DropdownTrigger
                                    className={
                                        "ml-2 self-start items-center min-w-[100px] cursor-pointer"
                                    }
                                >
                                    <div className="flex">
                                        <Avatar src={user.avatar} name={user.name} size={40} />
                                        <div
                                            className={classNames(
                                                "flex justify-between items-center w-full pr-2 duration-300 ",
                                                // {
                                                //     "opacity-0": !isExpanded,
                                                //     "opacity-100": isExpanded,
                                                // }
                                            )}
                                        >
                                            <p className="px-2 text-sm min-w-[100px]">
                                                {truncate(user.name, {
                                                    length: 20,
                                                })}
                                            </p>
                                            <ChevronDown className="w-4 h-4 text-gray-400" />
                                        </div>
                                    </div>
                                </DropdownTrigger>
                                <DropdownMenu
                                    aria-label="Static Actions"
                                    className="!rounded-md "
                                    disabledKeys={["info"]}
                                >
                                    {getDropdownMenuItems()}
                                </DropdownMenu>
                            </Dropdown>


                        </div>
                    </div>
                </div>
            </div>
        </div>);
};

export default TopNavigation;
